import React from "react";
import {
  FileText,
  Scale,
  Lock,
  CreditCard,
  Users,
  ShieldCheck,
  Truck,
  AlertCircle,
  Heart,
  Gavel,
  UserX,
  Info,
  MapPin,
  Mail,
  Phone,
} from "lucide-react";

const TermsAndConditions = () => {
  const currentDate = new Date().toLocaleDateString("en-IN", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="min-h-screen bg-[#FFF0F3] text-gray-800 font-sans selection:bg-rose-200">
      <div className="h-2 bg-rose-500 w-full sticky top-0 z-50 shadow-md" />

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header Section */}
        <header className="text-center mb-16">
          <div className="inline-block px-5 py-2 rounded-full bg-rose-100 text-rose-700 text-sm font-extrabold mb-6 tracking-wider uppercase shadow-sm border border-rose-200">
            Legal Agreement
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-5 tracking-tight leading-tight">
            Terms of <span className="text-rose-500">Service</span>
          </h1>
          <p className="text-xl text-gray-700 font-medium italic mb-6">
            Eveya – A brand of Farmfit Agrihealth LLP
          </p>
          <div className="flex justify-center items-center gap-6 text-sm font-medium text-gray-600 flex-wrap">
            <span className="bg-white px-4 py-1.5 rounded-full border border-rose-200 shadow-sm">
              Last Updated: {currentDate}
            </span>
          </div>
        </header>

        <div className="space-y-10">
          {/* 1. INTRODUCTION */}
          <Section
            title="1. INTRODUCTION & AGREEMENT ACCEPTANCE"
            icon={<FileText size={28} className="text-rose-500" />}
          >
            <div className="space-y-4">
              <p>
                <strong>1.1</strong> This Terms of Service ("Terms") forms a
                binding agreement between “Eveya” a brand from Farmfit
                Agrihealth LLP, with its registered office at 12-137/1, Flat No:
                402, Atluri Avenue, KVR Colony 1st Road, Enikepadu, Vijayawada,
                NTR District, Andhra Pradesh-521108, India (hereinafter referred
                to as "Company, Firm, we, us, our) and you, the user, customer,
                or visitor (hereinafter referred to as you, your, User,
                subscriber, Customer) to our website/app from a computer or
                mobile phone device (including but not limited to, through
                windows, iOS or Android application).
              </p>
              <p>
                <strong>1.2</strong> By accessing our website/mobile application
                at www.eveya.in, creating an account, browsing products, or
                making any purchase, you acknowledge and agree to be bound by
                these Terms.
              </p>
              <p>
                <strong>1.3</strong> If you do not agree to these Terms, you
                must NOT use our platform. Your continued use constitutes
                acceptance.
              </p>
              <div className="bg-rose-50 p-5 rounded-xl border-l-4 border-rose-500">
                <p className="font-bold text-rose-700 mb-1">1.4 IMPORTANT:</p>
                <p className="text-sm">
                  These Terms include a clause requiring automatic renewal and
                  pre-authorized payments. Please read this carefully (see
                  Section 2.4).
                </p>
              </div>
              <p>
                <strong>1.5 Age & Legal Capacity:</strong>
              </p>
              <ul className="list-disc pl-8 space-y-1">
                <li>You must be at least 18 years old to use this platform.</li>
                <li>
                  You represent that you are legally competent to enter into
                  contracts under Indian Contract Act, 1872.
                </li>
                <li>
                  If you are a minor, you cannot subscribe to our services.
                </li>
              </ul>
              <p>
                <strong>1.6 Modification of Terms:</strong>
              </p>
              <ul className="list-disc pl-8 space-y-1">
                <li>
                  We reserve the right to modify these Terms at any time without
                  prior notice.
                </li>
                <li>
                  Material changes will be communicated via email or prominent
                  notification on the website.
                </li>
                <li>
                  Your continued use after 7 days of notice constitutes
                  acceptance of modified terms.
                </li>
                <li>
                  For subscription customers, we will provide 30 days' notice
                  for material changes.
                </li>
              </ul>
            </div>
          </Section>

          {/* 2. PRODUCTS & SERVICES */}
          <Section
            title="2. Products, Services & Availability"
            icon={<Scale size={28} className="text-rose-500" />}
          >
            <div className="space-y-4">
              <p>
                <strong>2.1 Product Information & Accuracy:</strong> We endeavor
                to provide accurate product descriptions, expiry dates, and
                pricing information. However, we do not warrant that all
                information is accurate, complete, or error-free. We reserve the
                right to correct pricing errors and discontinue products without
                prior notice. Product images are representative; actual
                packaging may vary.
              </p>
              <p>
                <strong>2.2 Health Claims & Disclaimers:</strong> Our products
                are menstrual hygiene solutions and NOT medical devices or
                medications. We make NO medical claims. If you have specific
                health concerns, consult a qualified healthcare professional.
                Our products comply with Bureau of Indian Standards (BIS) and
                relevant health regulations. By using our products, you
                acknowledge sole responsibility for assessing suitability for
                your personal use.
              </p>
              <p>
                <strong>2.3 Stock & Availability:</strong> Stock availability is
                not guaranteed. We may cancel orders if products become
                unavailable. In case of cancellation, we will issue a full
                refund within 5-7 business days.We reserve the right to limit
                quantities per customer. For subscription orders, we commit to
                maintaining inventory but reserve the right to substitute with
                equivalent products if required, with prior customer notice.
              </p>

              <div className="mt-6 pt-6 border-t border-rose-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  2.4 Subscription Services - Critical Terms:
                </h3>

                <p className="font-bold text-rose-600 mb-2">
                  A. AUTOMATIC RENEWAL & BILLING:
                </p>
                <ul className="list-disc pl-8 mb-4 space-y-1">
                  <li>
                    Your subscription will automatically renew on the date you
                    selected unless you cancel.
                  </li>
                  <li>
                    You will be charged the subscription fee on each renewal
                    date.
                  </li>
                  <li>
                    Billing occurs on the SAME DATE each month/period you
                    selected.
                  </li>
                  <li>
                    You MUST provide explicit consent via checkbox (NOT
                    pre-checked) before enrolling.
                  </li>
                </ul>

                <p className="font-bold text-rose-600 mb-2">
                  B. PRE-AUTHORIZED PAYMENT MANDATE (RBI Compliance):
                </p>
                <ul className="list-disc pl-8 mb-4 space-y-1 text-sm">
                  <li>
                    <strong>Credit/Debit Cards:</strong> You authorize your bank
                    to debit your account on the renewal date. You MUST provide
                    Additional Factor of Authentication (AFA) via OTP for the
                    first transaction. RBI requires notification 24 hours BEFORE
                    each recurring debit. We will send SMS/email reminder 48
                    hours before renewal with option to cancel.
                  </li>
                  <li>
                    <strong>UPI Payments:</strong> Each recurring transaction
                    requires your explicit approval. You must confirm each
                    recurring charge in your UPI app.
                  </li>
                  <li>
                    <strong>Wallets:</strong> Same AFA and notification
                    requirements apply. We will obtain explicit consent before
                    auto-charging.
                  </li>
                </ul>

                <p className="font-bold text-rose-600 mb-2">
                  C. SUBSCRIPTION PLANS & MODIFICATIONS:
                </p>
                <div className="overflow-x-auto rounded-xl border border-rose-200 mb-4">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-rose-500 text-white">
                      <tr>
                        <th className="p-3 border-r border-rose-400">Plan</th>
                        <th className="p-3 text-center border-r border-rose-400">
                          Starter
                        </th>
                        <th className="p-3 text-center border-r border-rose-400">
                          Essential
                        </th>
                        <th className="p-3 text-center border-r border-rose-400">
                          Popular
                        </th>
                        <th className="p-3 text-center">Premium</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      <tr className="border-b border-rose-100">
                        <td className="p-3 font-bold bg-rose-50">
                          3-Months (₹13/Pad)
                        </td>
                        <td className="p-3 text-center">₹259.99 (20 Pads)</td>
                        <td className="p-3 text-center">₹389.99 (30 Pads)</td>
                        <td className="p-3 text-center">₹584.99 (45 Pads)</td>
                        <td className="p-3 text-center">₹779.99 (60 Pads)</td>
                      </tr>
                      <tr className="border-b border-rose-100">
                        <td className="p-3 font-bold bg-rose-50">
                          6-Months (₹11.5/Pad)
                        </td>
                        <td className="p-3 text-center">₹459.99 (40 Pads)</td>
                        <td className="p-3 text-center">₹689.99 (60 Pads)</td>
                        <td className="p-3 text-center">₹1034.99 (90 Pads)</td>
                        <td className="p-3 text-center">₹1378.99 (120 Pads)</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-bold bg-rose-50">
                          12-Months (₹10.5/Pad)
                        </td>
                        <td className="p-3 text-center">₹839.99 (80 Pads)</td>
                        <td className="p-3 text-center">₹1259.99 (120 Pads)</td>
                        <td className="p-3 text-center">₹1889.99 (180 Pads)</td>
                        <td className="p-3 text-center">₹2519.99 (240 Pads)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm italic mb-4">
                  You can change or cancel your subscription anytime. Changes
                  take effect on the NEXT billing cycle.
                </p>

                <p className="font-bold text-rose-600 mb-2">
                  D. PRICING & DISCOUNTS:
                </p>
                <p className="text-sm mb-4">
                  Subscription prices are clearly displayed before purchase.
                  Promotional discounts apply only to the first billing cycle
                  (unless otherwise stated). Subsequent renewals occur at the
                  standard subscription price. We reserve the right to increase
                  subscription prices with 30 days' notice. Any price increase
                  will not apply to active subscriptions.
                </p>

                <p className="font-bold text-rose-600 mb-2">
                  E. CORPORATE & B2B SUBSCRIPTIONS:
                </p>
                <p className="text-sm">
                  Bulk pricing applies per agreement.An authorized signatory
                  from the corporate entity must accept these terms.Payment
                  terms may differ (invoicing, net 30, etc.).Customized
                  products/packaging may incur additional charges.Corporate
                  customers have a dedicated grievance officer (Section 5)
                </p>
              </div>
            </div>
          </Section>

          {/* 3. SAKHI PROGRAM */}
          <Section
            title="3. SAKHI SPONSORSHIP PROGRAM"
            icon={<Heart size={28} className="text-rose-500" />}
          >
            <div className="space-y-4">
              <p>
                <strong>3.1 What is it?</strong> Sakhi Sponsorship is where
                customers can sponsor menstrual hygiene products for women in
                need. It's a charitable initiative, NOT a tax-deductible
                donation.
              </p>
              <p>
                <strong>3.2 No Tax Benefit:</strong> Sponsorships are NOT
                tax-deductible. You will NOT receive a tax reduction receipt.
                You cannot claim any tax deduction.
              </p>
              <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                <p>
                  <strong>3.3 NON-REFUNDABLE:</strong> All Sakhi sponsorships
                  are FINAL and NON-REFUNDABLE. Products shall be donated after
                  payment. No refunds issued under any circumstances.
                </p>
              </div>
              <p>
                <strong>3.4 How it works:</strong> You pay sponsorship amount →
                We donate to women directly or through verified partners → You
                receive impact updates.
              </p>
            </div>
          </Section>

          {/* 4. PRICING & BILLING */}
          <Section
            title="4. Pricing, Payment & Billing"
            icon={<CreditCard size={28} className="text-rose-500" />}
          >
            <div className="space-y-4">
              <p>
                <strong>4.1 Price Display:</strong> All prices are displayed in
                Indian Rupees (INR) and include GST unless stated otherwise.
                Additional charges (shipping,packaging) are itemized before
                checkout. Bundle discounts are applied automatically in the
                cart.
              </p>
              <p>
                <strong>4.2 Payment Methods:</strong> We accept Credit/Debit
                Cards(Visa, Mastercard, American Express), Net Banking,
                UPI(Google Pay, PhonePe, Paytm, etc.), and Digital Wallets. Bank
                transfers accepted for corporate orders.
              </p>
              <p>
                <strong>4.3 Payment Processing:</strong> Payments are processed
                by Zoho and PhonePe.Upon successful payment, you will receive a
                confirmation email with order details and invoice.Disputes must
                be raised within 60 days through support or your bank.Failed
                payments due to insufficient funds or card issues are your
                responsibility.
              </p>
              <p>
                <strong>4.4 Failed Transactions:</strong> If renewal fails, we
                retry 2 times over 3 days. If retries fail, your subscription is
                CANCELLED.You will receive notification of cancellation via
                SMS/email.You can manually reactivate by updating payment info.
              </p>
              <p>
                <strong>4.5 Refund Timeline:</strong> Cards (5-7 days),
                UPI/Wallets (3-4 days), Bank Transfer (5-10 days).
              </p>
              <p>
                Note: Bank processing times are beyond our control; please
                contact your bank if refund doesn't appear within this
                window.{" "}
              </p>
              <p>
                <strong>4.6 International:</strong> We serve only Indian
                customers (INR). We do NOT accept international payments or
                orders at this time. Customers outside India cannot order from
                our platform.
              </p>
            </div>
          </Section>

          {/* 5. SHIPPING */}
          <Section
            title="5. Order Processing, Shipping and Delivery"
            icon={<Truck size={28} className="text-rose-500" />}
          >
            <div className="space-y-4">
              <p>
                <strong>5.1 Order Confirmation:</strong> Confirmed only after
                payment. You will receive an email within 2 hours with tracking
                information. Order details include product list, quantity,
                delivery address, and tracking information.
              </p>
              <p>
                <strong>5.2 Processing:</strong> Orders processed in 1-2
                business days. Subscription orders will be dispatched only upon
                ordering from the website, no automatic dispatch will be
                initiated unless and until ordering was done through the
                specified user account from the website. You will receive a
                tracking number via SMS/email upon dispatch. Processing delays
                may occur during peak seasons; we will notify you of any delays
                exceeding 5 days.
              </p>
              <div className="bg-rose-50 p-5 rounded-xl border border-rose-100">
                <p className="font-bold mb-2">5.3 Shipping Charges:</p>
                <ul className="list-disc pl-8 space-y-1">
                  <li>Delivery is provided across India.</li>
                  <li>
                    Delivery timeframe: 3-7 business days from dispatch date
                    (depending on location).{" "}
                  </li>
                  <li>
                    We are NOT responsible for delivery delays caused by courier
                    partners or natural calamities.
                  </li>
                  <li>
                    Tracking updates are sent via SMS/WhatsApp. You can also
                    track orders in specified link provided via
                    SMS/WhatsApp.{" "}
                  </li>
                  <li>
                    <strong>Subscribers:</strong> First delivery is FREE in the
                    subscription period. All other orders in that period carry a
                    fixed ₹50 per delivery as standard.
                  </li>
                  <li>
                    <strong>Non-Subscribers:</strong> Amount shown during
                    checkout (the amount varies based on the destination and
                    weight of the order). No free delivery applies.
                  </li>
                </ul>
              </div>
              <p>
                <strong>5.4 Address Changes:</strong> Must provide a valid
                delivery address at checkout. No address changes permitted after
                dispatch.For subscriptions, the delivery address is locked
                UNLESS you proactively update it in your account before
                ordering.
              </p>
              <p>
                <strong>5.5 Damaged Products:</strong> If a package is
                tampered,unopened or damaged, notify us{" "}
                <strong>WITHIN 24 HOURS</strong> with photos. We will either
                replace or refund such products at no cost. Damage claims after
                24 hours cannot be processed.
              </p>
              <p>
                <strong>5.6 Packaging:</strong> Packaging is discreet to protect
                your privacy. Subscription boxes include packing slips but NO
                invoices (emailed separately for security).You acknowledge
                receiving the product and assume responsibility for secure
                storage upon delivery
              </p>
            </div>
          </Section>

          {/* 6. RETURNS placeholder */}
          <Section
            title="6. RETURNS, REFUNDS & CANCELLATIONS"
            icon={<AlertCircle size={28} className="text-rose-500" />}
          >
            <p className="italic text-gray-600">
              Please refer to our detailed Returns & Refunds Policy page for
              specific procedures and eligibility.
            </p>
          </Section>

          {/* 7. INTELLECTUAL PROPERTY */}
          <Section
            title="7. Intellectual Property & Content Rights"
            icon={<Lock size={28} className="text-rose-500" />}
          >
            <p>
              <strong>7.1 Company IP:</strong> All content—logos, taglines,
              product photography, blog posts, videos, trademarks, designs — is
              owned by [Farmfit Agrihealth LLP] or our licensors. You may NOT
              reproduce, redistribute, or use any content without written
              permission. Unauthorized use may result in legal action.
            </p>
            <p>
              <strong>7.2 User Content:</strong> If you post reviews,
              testimonials, or images on our platform, you grant us a
              non-exclusive, royalty-free license to use, modify, and publish
              this content globally (including on social media). You represent
              that you own the content and have the right to grant this license.
              We may use your review/image in marketing (with or without
              crediting you). You can request removal of your content via the
              "Report" button.
            </p>
            <p>
              <strong>7.3 Third-Party Links:</strong> Our website may contain
              links to third-party websites or landing pages. We are NOT
              responsible for third-party content, privacy practices, or
              services. Clicking third-party links means leaving our platform;
              read their terms carefully. <p>Orders, deliveries, and services
              facilitated through partners or third-party websites,
              applications, or service providers are subject to their respective
              operational coverage, policies, and serviceability in particular
              locations. Such services are independently managed by the
              respective providers, and we disclaim any responsibility for their
              availability, fulfillment, timelines, or service limitations in
              any area.</p>
            </p>
          </Section>

          {/* 8. LIABILITIES */}
          <Section
            title="8. Limitations of Liabilities and Disclaimers"
            icon={<ShieldCheck size={28} className="text-rose-500" />}
          >
            <div className="space-y-4">
              <p>
                <strong>8.1 "AS IS" Disclaimer:</strong> Our platform and
                products are provided "AS IS" and "AS AVAILABLE" without
                warranties. We do not warrant that: - The website will be
                uninterrupted or error-free - All products will meet your
                expectations - Products are suitable for all skin types
                (allergies vary by person)
              </p>
              <p>
                <strong>8.2 Limitation:</strong> Except where prohibited by law,
                our liability for any claim arising from these Terms or platform
                use shall NOT EXCEED the total amount you paid us in the 12
                months preceding the claim. We are NOT liable for: - Indirect,
                incidental, or consequential damages - Loss of data, profits, or
                business - Third-party claims or actions - Issues arising from
                misuse of products - Allergic reactions or skin sensitivity (you
                assume this risk)
              </p>
              <p>
                <strong>8.3 Your Responsibility:</strong> You assume all risk
                associated with using our platform and products. You are
                responsible for maintaining confidentiality of login
                credentials.You agree NOT to: - Disclose health/menstrual
                information of others without consent - Resell products
                purchased for personal use - Violate any applicable laws -
                Attempt to breach our systems or access unauthorized content
              </p>
              <p>
                <strong>8.4 Force Majeure:</strong> Not liable for
                non-performance due to unforeseeable events (disasters,
                pandemics, etc.).We will inform you of such events and provide
                alternative solutions if possible.      
              </p>
            </div>
          </Section>

          {/* 9. PRIVACY */}
          <Section
            title="9. Privacy and Data Protection"
            icon={<Users size={28} className="text-rose-500" />}
          >
            <p>
              <strong>9.1 Data Processing:</strong> We collect and process
              personal data (name, email, address, payment info, health
              preferences) to deliver services and comply with law. We are
              committed to protecting your data under the Digital Personal Data
              Protection (DPDP) Act, 2023. Your data is NOT sold to third
              parties for marketing purposes. For details see privacy policy.
              Your personal data like name, phone number, email id shall be
              shared with our partners for providing subscription services only
              after taking the assent from you.
            </p>
            <p>
              <strong>9.2 Health Data Sensitivity:</strong> As a women's hygiene
              platform, we may process health-related information (menstrual
              cycle, preferences, allergies). This data is processed with the
              highest confidentiality. We collect and process this data only to
              recommend products which suites you the best. You have the right
              to request, access, correct, or delete your data.	See Privacy Policy for the full data rights section
            </p>
          </Section>

          {/* 10. PROHIBITED CONDUCT */}
          <Section
            title="10. Prohibited Users and Conduct"
            icon={<UserX size={28} className="text-rose-500" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="p-3 border border-rose-100 rounded-lg">
                <strong>10.1 Fraud:</strong> Use stolen payment information
                Attempt chargebacks or payment disputes on legitimate
                transactions Create multiple accounts to exploit discounts or
                bypass limits Commit identity fraud
              </div>
              <div className="p-3 border border-rose-100 rounded-lg">
                <strong>10.2 System Abuse:</strong> Attempt to hack, penetrate,
                or breach our website/app security Use bots or automated tools
                to scrape data Distribute malware or viruses Overload servers
                with requests
              </div>
              <div className="p-3 border border-rose-100 rounded-lg">
                <strong>10.3 Inappropriate Content:</strong> Post hateful,
                abusive, or defamatory content Post sexual or explicit content
                (product-related discussion is permitted) Harass other customers
                or staff Spam or promote competing products
              </div>
              <div className="p-3 border border-rose-100 rounded-lg">
                <strong>10.4 IP Violation:</strong> Counterfeit products Sell
                products in violation of copyright/trademark Distribute products
                obtained illegally
              </div>
            </div>
            <p className="mt-4 text-sm font-bold text-rose-600">
              10.5 Consequences: Violations may result in account suspension or
              termination. We may report illegal activity to law enforcement.
              You remain liable for damages caused by violations. No refund will
              be issued for suspended accounts due to violation
            </p>
          </Section>

          {/* 11. DISPUTE RESOLUTION */}
          <Section
            title="11. DISPUTE RESOLUTION & GRIEVANCE MECHANISM"
            icon={<Gavel size={28} className="text-rose-500" />}
          >
            <div className="space-y-4">
              <div className="bg-white p-5 border border-rose-200 rounded-xl shadow-sm">
                <p className="font-bold mb-2">
                  11.1 Informal Resolution (Required First Step):
                </p>
                <p className="text-sm">
                  Contact Grievance Officer: <strong>support@eveya.in</strong> |
                  +91 8121514499
                </p>
                <p className="text-xs text-gray-500">
                  Working Hours: Mon-Sat, 10 AM - 6 PM IST. Resolution attempt
                  within 15 days.
                </p>
                <p>
                  Response Timeframe: Within 48 hours.Provide: - Your name,
                  email, order number - Clear description of the issue -
                  Supporting documentation (screenshots, order proof, etc.)
                </p>
              </div>
              <p>
                <strong>11.2 Escalation:</strong> If unresolved within 15 days,
                you can escalate to our Chief Grievance Redressal Officer. We
                will engage neutral mediation if both parties agree. Mediation
                attempts must occur within 30 days.
              </p>
              <p>
                <strong>11.3 Legal Jurisdiction:</strong> Governed by Indian
                Law. EXCLUSIVE JURISDICTION of courts in{" "}
                <strong>Vijayawada, Andhra Pradesh</strong>. • However, for
                consumer matters, you may approach the District Consumer
                Commission under Consumer Protection Act, 2019. Consumer
                Redressal Options: District Consumer Dispute Redressal
                Commission (for claims up to ₹1 crore) State Consumer Dispute
                Redressal Commission (for claims ₹1-10 crore) National Consumer
                Dispute Redressal Commission (for claims above ₹10 crore){" "}
              </p>
              <p>
                <strong>11.4 Binding Arbitration:</strong> Alternatively,
                disputes may be resolved through binding arbitration under
                Arbitration and Conciliation Act, 1996. Arbitrator shall be
                mutually selected. Arbitration venue: Vijayawada Decision is
                final and binding. (Note: Consumer disputes cannot be arbitrated
                if customer chooses Commission route)
              </p>
              <p>
                <strong>11.5 SLA:</strong>For subscription customers, we provide
                dedicated grievance support. Issue types: Billing issues,
                delivery problems, product quality, cancellation requests.
                Resolution SLA: 7 days for non-complex issues, 15 days for
                complex issues
              </p>
            </div>
          </Section>

          {/* 12. TERMINATION */}
          <Section
            title="12. Termination and Account Closure"
            icon={<UserX size={28} className="text-rose-500" />}
          >
            <p>
              <strong>12.1 Customer-Initiated:</strong> You can close your
              account anytime by emailing support or using the "Delete Account"
              option in settings. Closing your account cancels any active
              subscriptions. Refunds are processed per the Refund Policy. You
              remain responsible for any outstanding payments.
            </p>
            <p>
              <strong>12.2 Company-Initiated:</strong> We may terminate for
              fraud or policy violations. Violate these Terms (Section 9)
              ,Engage in fraudulent behavior ,Repeatedly violate payment terms
              ,Repeatedly file frivolous chargebacks ,Harass staff or other
              customers ,Notice: We will provide 7 days' written notice (email)
              before termination for policy violations (except fraud, which is
              immediate). ,Termination means you lose access to your account and
              any stored information. ,No refund is issued for accounts
              terminated due to violation (except for legitimate disputes).
            </p>
            <p>
              <strong>12.3 Data Retention:</strong> We retain your data per
              legal requirements (GST, tax law: 6 years minimum). For
              subscription records: retained for 2 years for dispute resolution.
              Personal data is anonymized after retention period. See Privacy
              Policy for full data retention schedule
            </p>
          </Section>

          {/* 13. GENERAL */}
          <Section
            title="13. General Provisions"
            icon={<Info size={28} className="text-rose-500" />}
          >
            <p>
              <strong>13.1 Governing Law:</strong> Consumer Protection Act 2019,
              Indian Contract Act 1872, DPDP Act 2023, E-Commerce Rules 2020.
            </p>
            <p>
              <strong>13.2 Severability:</strong> If one provision is invalid,
              the rest remain in effect. We will replace the invalid provision
              with one as close as possible to the original intent.
            </p>
            <p>
              <strong>13.3 No Waiver:</strong> Failure to enforce any right does
              not waive that right for future use.
            </p>
            <p>
              <strong>13.4 Entire Agreement: </strong>These Terms + Privacy
              Policy + Refund Policy constitute the entire agreement. No verbal
              assurances override written terms.
            </p>
            <div className="mt-8 p-6 bg-white rounded-2xl border border-rose-200">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin size={18} /> 13.5 Contact Information
              </h4>
              <p className="font-bold">Farmfit Agrihealth LLP</p>
              <p className="text-sm text-gray-600">
                12-137/1, Flat No: 402, Atluri Avenue, KVR Colony 1st Road,
                Enikepadu, Vijayawada, NTR District, Andhra Pradesh-521108,
                India.
              </p>
              <div className="flex flex-wrap gap-6 mt-4">
                <span className="flex items-center gap-2 text-sm text-rose-600 font-medium">
                  <Mail size={16} /> support@eveya.in
                </span>
                <span className="flex items-center gap-2 text-sm text-rose-600 font-medium">
                  <Phone size={16} /> +91 8121514499
                </span>
              </div>
            </div>
          </Section>
        </div>

        {/* <footer className="mt-16 text-center text-gray-500 text-xs border-t border-rose-200 pt-8">
          <p>
            © {new Date().getFullYear()} Farmfit Agrihealth LLP. All Rights
            Reserved. "Eveya" is a registered trademark.
          </p>
        </footer> */}
      </div>
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <section className="bg-white rounded-3xl p-8 border border-rose-100 shadow-lg shadow-rose-200/20 hover:shadow-rose-300/30 transition-all duration-300">
    <div className="flex items-center gap-4 mb-6">
      {icon && <div className="p-3 bg-rose-50 rounded-2xl">{icon}</div>}
      <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-tight">
        {title}
      </h2>
    </div>
    <div className="text-gray-700 leading-relaxed space-y-4">{children}</div>
  </section>
);

export default TermsAndConditions;
